/**
 * @fileoverview Socket.io client service for real-time communication.
 *
 * This module manages the WebSocket connection to the backend server
 * for real-time updates. It handles:
 * - Connection with authentication
 * - Automatic reconnection
 * - Budget room management
 * - Event subscription
 *
 * @module services/socket
 */

import { io } from 'socket.io-client';

// Get the API URL from environment or default to localhost
// Use REACT_APP_API_BASE_URL (same as REST API) but strip '/api' suffix for socket connection
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const SOCKET_URL = API_BASE.replace(/\/api\/?$/, '');

/** @type {Socket|null} Current socket instance */
let socket = null;

/** @type {Function|null} Token getter function from Auth0 */
let tokenGetter = null;

/** @type {number} Reconnection attempt counter */
let reconnectAttempts = 0;

/** @type {number} Maximum reconnection attempts before giving up */
const MAX_RECONNECT_ATTEMPTS = 10;

/**
 * Set the token getter function for authentication.
 * Call this once when the app initializes with Auth0.
 *
 * @param {Function} getter - Async function that returns an access token
 *
 * @example
 * import { useAuth0 } from '@auth0/auth0-react';
 *
 * const { getAccessTokenSilently } = useAuth0();
 * setTokenGetter(getAccessTokenSilently);
 */
export function setTokenGetter(getter) {
    tokenGetter = getter;
}

/**
 * Initialize the socket connection.
 *
 * @returns {Promise<Socket>} The connected socket instance
 * @throws {Error} If token getter is not set or connection fails
 */
export async function initializeSocket() {
    if (!tokenGetter) {
        throw new Error('Token getter not set. Call setTokenGetter first.');
    }

    // Already connected
    if (socket?.connected) {
        return socket;
    }

    // Get fresh token for authentication
    const token = await tokenGetter();

    socket = io(SOCKET_URL, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
    });

    return new Promise((resolve, reject) => {
        socket.on('connect', () => {
            console.log('[Socket] Connected:', socket.id);
            reconnectAttempts = 0;
            resolve(socket);
        });

        socket.on('connect_error', async (error) => {
            console.error('[Socket] Connection error:', error.message);
            reconnectAttempts++;

            // If auth error, try refreshing token
            if (error.message.includes('Authentication') && tokenGetter) {
                try {
                    const newToken = await tokenGetter();
                    socket.auth = { token: newToken };
                } catch (tokenError) {
                    console.error('[Socket] Token refresh failed:', tokenError);
                }
            }

            if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
                reject(new Error('Failed to connect after max attempts'));
            }
        });

        socket.on('disconnect', (reason) => {
            console.log('[Socket] Disconnected:', reason);
        });

        socket.on('error', (error) => {
            console.error('[Socket] Error:', error);
        });
    });
}

/**
 * Get the current socket instance.
 *
 * @returns {Socket|null} The socket instance, or null if not connected
 */
export function getSocket() {
    return socket;
}

/**
 * Check if socket is connected.
 *
 * @returns {boolean} True if connected
 */
export function isConnected() {
    return socket?.connected || false;
}

/**
 * Get the current socket ID.
 * Used for filtering out own events on multi-device scenarios.
 *
 * @returns {string|null} Socket ID or null if not connected
 */
export function getSocketId() {
    return socket?.id || null;
}

/**
 * Join a budget room to receive updates for that budget.
 *
 * @param {number} budgetKey - Budget ID to join
 */
export function joinBudget(budgetKey) {
    if (socket?.connected) {
        socket.emit('budget:join', { budgetKey });
    }
}

/**
 * Leave the current budget room.
 */
export function leaveBudget() {
    if (socket?.connected) {
        socket.emit('budget:leave');
    }
}

/**
 * Update the current month view.
 *
 * @param {string} monthKey - Month in YYYY-MM format
 */
export function viewMonth(monthKey) {
    if (socket?.connected) {
        socket.emit('month:view', { monthKey });
    }
}

/**
 * Notify that user started editing a resource.
 *
 * @param {string} resourceType - Type of resource ('transaction', 'category')
 * @param {number} resourceKey - ID of the resource
 */
export function startEdit(resourceType, resourceKey) {
    if (socket?.connected) {
        socket.emit('edit:start', { resourceType, resourceKey });
    }
}

/**
 * Notify that user stopped editing.
 */
export function endEdit() {
    if (socket?.connected) {
        socket.emit('edit:end');
    }
}

/**
 * Request sync events since a timestamp (for reconnection).
 *
 * @param {string} sinceTimestamp - ISO timestamp
 */
export function requestSync(sinceTimestamp) {
    if (socket?.connected) {
        socket.emit('sync:request', { sinceTimestamp });
    }
}

/**
 * Send a heartbeat to keep the connection alive in the database.
 * This prevents the cleanup job from removing active but idle connections.
 */
export function sendHeartbeat() {
    if (socket?.connected) {
        socket.emit('heartbeat');
    }
}

/**
 * Subscribe to a socket event.
 *
 * @param {string} event - Event name
 * @param {Function} callback - Event handler
 * @returns {Function} Unsubscribe function
 */
export function subscribe(event, callback) {
    if (socket) {
        socket.on(event, callback);
        return () => socket.off(event, callback);
    }
    return () => {};
}

/**
 * Disconnect the socket.
 */
export function disconnectSocket() {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
}
